import React, { useEffect, useState } from 'react';
import {
    Button, DialogActions, DialogContent, Stack, TextField, FormControlLabel, Checkbox,
    Grid, Typography, IconButton, Select, MenuItem, InputLabel, FormControl, Chip, Box, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DialogHeader from '../../../components/Dialog/DialogHeader';
import UserDialog from '../../../components/Dialog/UserDialog';
import {
    DictWeapon,
    DictWeaponTraitRef,
    DictWeaponUpgradeRef,
    CreateDictWeaponDto,
    CreateDictWeaponProfile,
} from '../../../model/Dto/Dict/Weapon';
import { Api } from '../../../request/api/api';

const weaponTypeOptions = [
    { value: 1, label: 'Basic' }, { value: 2, label: 'Pistol' }, { value: 3, label: 'Special' },
    { value: 4, label: 'Heavy' }, { value: 5, label: 'CloseCombat' }, { value: 6, label: 'Grenade' },
    { value: 7, label: 'AugmeticWeapons' }, { value: 8, label: 'Attachments' }, { value: 9, label: 'Innate' },
];

interface WeaponDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CreateDictWeaponDto) => Promise<boolean>;
    initialData?: DictWeapon | null;
}

interface FormWeaponProfile {
    id?: string;
    name: string;
    isDefault: boolean;
    cost: string;
    rarity: string;
    sr: string;
    lr: string;
    sm: string;
    lm: string;
    str: string;
    ap: string;
    d: string;
    am: string;
    traits: DictWeaponTraitRef[];
}

const emptyProfile = (): FormWeaponProfile => ({
    name: '',
    isDefault: false,
    cost: '0',
    rarity: '',
    sr: '',
    lr: '',
    sm: '',
    lm: '',
    str: '',
    ap: '',
    d: '',
    am: '',
    traits: [],
});

export default function WeaponDialog({ open, onClose, onSave, initialData }: WeaponDialogProps) {
    const [name, setName] = useState('');
    const [cost, setCost] = useState('0');
    const [rarity, setRarity] = useState('');
    const [type, setType] = useState<number>(1);
    const [isAvailableOnTradingPost, setIsAvailableOnTradingPost] = useState(false);
    const [profiles, setProfiles] = useState<FormWeaponProfile[]>([]);
    const [upgradeIds, setUpgradeIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [allUpgrades, setAllUpgrades] = useState<DictWeaponUpgradeRef[]>([]);
    const [allTraits, setAllTraits] = useState<DictWeaponTraitRef[]>([]);

    // Загрузка справочников для селектов
    useEffect(() => {
        if (open) {
            Promise.all([Api.dict.getAllUpgrades(), Api.dict.getAllTraits()])
                .then(([upgrades, traits]) => {
                    setAllUpgrades(upgrades.map(u => ({ id: u.id, name: u.name })));
                    setAllTraits(traits.map(t => ({ id: t.id, name: t.name })));
                });
        }
    }, [open]);

    // Заполнение формы при редактировании
    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setCost(String(initialData.cost));
            setRarity(initialData.rarity === null ? '' : String(initialData.rarity));
            setType(initialData.type);
            setIsAvailableOnTradingPost(initialData.isAvailableOnTradingPost);
            setProfiles(initialData.profiles.map(p => ({
                id: p.id,               // сохраняем ExternalId для существующих профилей
                name: p.name,
                isDefault: p.isDefault,
                cost: String(p.cost),
                rarity: p.rarity === null ? '' : String(p.rarity),
                sr: p.sr,
                lr: p.lr,
                sm: p.sm,
                lm: p.lm,
                str: p.str,
                ap: p.ap,
                d: p.d,
                am: p.am,
                traits: p.traits,
            })));
            setUpgradeIds(initialData.upgrades.map(u => u.id));
        } else {
            // Новое оружие: один пустой профиль
            setName('');
            setCost('0');
            setRarity('');
            setType(1);
            setIsAvailableOnTradingPost(false);
            setProfiles([emptyProfile()]);
            setUpgradeIds([]);
        }
    }, [initialData, open]);

    const handleAddProfile = () => setProfiles([...profiles, emptyProfile()]);
    const handleRemoveProfile = (index: number) => {
        const newProfiles = [...profiles];
        newProfiles.splice(index, 1);
        setProfiles(newProfiles);
    };
    const handleProfileChange = (index: number, field: keyof FormWeaponProfile, value: any) => {
        const newProfiles = [...profiles];
        (newProfiles[index] as any)[field] = value;
        setProfiles(newProfiles);
    };
    const handleTraitsChange = (index: number, traitIds: string[]) => {
        const selectedTraits = allTraits.filter(t => traitIds.includes(t.id));
        const newProfiles = [...profiles];
        newProfiles[index].traits = selectedTraits;
        setProfiles(newProfiles);
    };

    const handleSave = async () => {
        setLoading(true);
        const data: CreateDictWeaponDto = {
            name: name.trim(),
            cost: Number(cost) || 0,
            rarity: rarity === '' ? null : Number(rarity),
            type,
            isAvailableOnTradingPost,
            profiles: profiles.map(p => ({
                profileId: p.id,               // для новых профилей undefined
                name: p.name,
                isDefault: p.isDefault,
                cost: Number(p.cost) || 0,
                rarity: p.rarity === '' ? null : Number(p.rarity),
                sr: p.sr,
                lr: p.lr,
                sm: p.sm,
                lm: p.lm,
                str: p.str,
                ap: p.ap,
                d: p.d,
                am: p.am,
                traits: p.traits.map(t => t.id),
            })),
            upgrades: upgradeIds,
        };
        const success = await onSave(data);
        setLoading(false);
        if (success) onClose();
    };

    return (
        <UserDialog handleClose={onClose} open={open} maxWidth="lg">
            <DialogHeader loading={loading} title={initialData ? 'Редактировать оружие' : 'Добавить оружие'} />
            <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Основные поля */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Название"
                                fullWidth
                                value={name}
                                onChange={e => setName(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Цена"
                                fullWidth
                                inputProps={{ inputMode: 'numeric' }}
                                value={cost}
                                onChange={e => setCost(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Редкость"
                                fullWidth
                                inputProps={{ inputMode: 'numeric' }}
                                value={rarity}
                                onChange={e => setRarity(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Тип оружия</InputLabel>
                                <Select value={type} onChange={e => setType(Number(e.target.value))} label="Тип оружия" disabled={loading}>
                                    {weaponTypeOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAvailableOnTradingPost}
                                        onChange={e => setIsAvailableOnTradingPost(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Доступно на торговом посту"
                            />
                        </Grid>
                    </Grid>
                    <Divider />

                    {/* Апгрейды */}
                    <FormControl fullWidth>
                        <InputLabel>Апгрейды</InputLabel>
                        <Select
                            multiple
                            value={upgradeIds}
                            onChange={e => setUpgradeIds(typeof e.target.value === 'string' ? [] : e.target.value)}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map(id => (
                                        <Chip key={id} label={allUpgrades.find(u => u.id === id)?.name || id} size="small" />
                                    ))}
                                </Box>
                            )}
                            disabled={loading}
                        >
                            {allUpgrades.map(up => <MenuItem key={up.id} value={up.id}>{up.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Divider />

                    {/* Профили */}
                    <Typography variant="h6">Профили оружия</Typography>
                    {profiles.map((profile, idx) => (
                        <Stack key={idx} spacing={2} sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1, position: 'relative' }}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveProfile(idx)}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                                disabled={loading || profiles.length === 1}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Название профиля"
                                        fullWidth
                                        value={profile.name}
                                        onChange={e => handleProfileChange(idx, 'name', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        label="Цена профиля"
                                        fullWidth
                                        inputProps={{ inputMode: 'numeric' }}
                                        value={profile.cost}
                                        onChange={e => handleProfileChange(idx, 'cost', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField
                                        label="Редкость профиля"
                                        fullWidth
                                        inputProps={{ inputMode: 'numeric' }}
                                        value={profile.rarity}
                                        onChange={e => handleProfileChange(idx, 'rarity', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={profile.isDefault}
                                                onChange={e => handleProfileChange(idx, 'isDefault', e.target.checked)}
                                                disabled={loading}
                                            />
                                        }
                                        label="По умолчанию"
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="S Range"
                                        fullWidth
                                        value={profile.sr}
                                        onChange={e => handleProfileChange(idx, 'sr', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="L Range"
                                        fullWidth
                                        value={profile.lr}
                                        onChange={e => handleProfileChange(idx, 'lr', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="S Acc"
                                        fullWidth
                                        value={profile.sm}
                                        onChange={e => handleProfileChange(idx, 'sm', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="L Acc"
                                        fullWidth
                                        value={profile.lm}
                                        onChange={e => handleProfileChange(idx, 'lm', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Strength"
                                        fullWidth
                                        value={profile.str}
                                        onChange={e => handleProfileChange(idx, 'str', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="AP"
                                        fullWidth
                                        value={profile.ap}
                                        onChange={e => handleProfileChange(idx, 'ap', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Damage"
                                        fullWidth
                                        value={profile.d}
                                        onChange={e => handleProfileChange(idx, 'd', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Ammo"
                                        fullWidth
                                        value={profile.am}
                                        onChange={e => handleProfileChange(idx, 'am', e.target.value)}
                                        disabled={loading}
                                    />
                                </Grid>
                            </Grid>
                            <FormControl fullWidth>
                                <InputLabel>Свойства (Trait)</InputLabel>
                                <Select
                                    multiple
                                    value={profile.traits.map(t => t.id)}
                                    onChange={e => handleTraitsChange(idx, typeof e.target.value === 'string' ? [] : e.target.value)}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map(id => (
                                                <Chip key={id} label={allTraits.find(t => t.id === id)?.name || id} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                    disabled={loading}
                                >
                                    {allTraits.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Stack>
                    ))}
                    <Button startIcon={<AddIcon />} onClick={handleAddProfile} disabled={loading}>
                        Добавить профиль
                    </Button>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {loading ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </DialogActions>
        </UserDialog>
    );
}